/**
 * ProvenanceTag -- reusable provenance tag component.
 *
 * Renders a QR code and/or ArUco marker either inline in the UI (as a
 * React component) or onto a canvas for image export. The QR encodes the
 * Thread asset URL so exported plots can be scanned back to their source.
 *
 * For ArUco: draws a 6x6 marker from the ARUCO_6X6_1000 dictionary
 * using a deterministic ID derived from the artifact hash.
 */

import { useRef, useEffect, useMemo, useCallback } from "react";
import { buildAssetUrl, embedPngMetadata, type ThreadMetadata } from "../../lib/threadMetadata";
import { drawQROnCanvas } from "./qrEncoder";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TagMode = "qr" | "aruco" | "both" | "none";

export interface ProvenanceTagProps {
  artifactId: string;
  projectId: string;
  mode?: TagMode;
  size?: number;
  showLabel?: boolean;
}

// ---------------------------------------------------------------------------
// ArUco 6x6 marker (simplified procedural generation)
// ---------------------------------------------------------------------------

/**
 * Generate a deterministic 6x6 bit pattern from an artifact ID.
 * Uses a simple hash to pick a pattern from the ArUco 6x6_1000 dictionary
 * space. The outer ring is always the black border; the inner 4x4 grid
 * carries the data bits.
 */
function generateArucoBits(artifactId: string): boolean[][] {
  // Simple hash of artifact ID -> 16-bit value for the inner 4x4
  let hash = 0;
  for (let i = 0; i < artifactId.length; i++) {
    hash = ((hash << 5) - hash + artifactId.charCodeAt(i)) | 0;
  }
  hash = Math.abs(hash) & 0xffff;

  // 6x6 grid: outer ring is black border, inner 4x4 is data
  const grid: boolean[][] = Array.from({ length: 6 }, () =>
    new Array(6).fill(true),
  );

  // Inner 4x4 data bits
  for (let r = 0; r < 4; r++) {
    for (let c = 0; c < 4; c++) {
      const bitIndex = r * 4 + c;
      grid[r + 1]![c + 1] = ((hash >> bitIndex) & 1) === 1;
    }
  }

  return grid;
}

function drawArucoOnCanvas(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  artifactId: string,
): void {
  const grid = generateArucoBits(artifactId);
  const cellSize = size / 8; // 6 cells + 1 cell white border on each side

  // White background
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(x, y, size, size);

  // Draw cells
  for (let r = 0; r < 6; r++) {
    for (let c = 0; c < 6; c++) {
      ctx.fillStyle = grid[r]![c] ? "#000000" : "#FFFFFF";
      ctx.fillRect(
        x + (c + 1) * cellSize,
        y + (r + 1) * cellSize,
        cellSize + 0.5,
        cellSize + 0.5,
      );
    }
  }
}

// ---------------------------------------------------------------------------
// Tag strip drawing (shared between UI preview and export)
// ---------------------------------------------------------------------------

const TAG_STRIP_HEIGHT = 36;
const TAG_PADDING = 8;
const LABEL_FONT = "bold 10px system-ui, -apple-system, sans-serif";
const URL_FONT = "9px system-ui, -apple-system, sans-serif";

/**
 * Draw a provenance tag strip onto a canvas context.
 * The strip contains a QR code (and/or ArUco marker) plus a "Thread" label
 * and the asset URL text.
 */
export async function drawProvenanceTag(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  artifactId: string,
  mode: string = "qr",
): Promise<void> {
  const tagMode = mode as TagMode;
  if (tagMode === "none") return;

  const url = buildAssetUrl(artifactId);
  const stripHeight = TAG_STRIP_HEIGHT;
  const codeSize = stripHeight - TAG_PADDING;

  // Background
  ctx.fillStyle = "#F8F9F8";
  ctx.fillRect(x, y, width, stripHeight);

  // Top border line
  ctx.strokeStyle = "#E6E9E7";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x, y + 0.5);
  ctx.lineTo(x + width, y + 0.5);
  ctx.stroke();

  let codeX = x + TAG_PADDING;

  // Draw QR code
  if (tagMode === "qr" || tagMode === "both") {
    drawQROnCanvas(ctx, url, codeX, y + TAG_PADDING / 2, codeSize);
    codeX += codeSize + TAG_PADDING;
  }

  // Draw ArUco marker
  if (tagMode === "aruco" || tagMode === "both") {
    drawArucoOnCanvas(ctx, codeX, y + TAG_PADDING / 2, codeSize, artifactId);
    codeX += codeSize + TAG_PADDING;
  }

  // Thread label
  ctx.fillStyle = "#111813";
  ctx.font = LABEL_FONT;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("Thread", codeX, y + 6);

  // Asset URL (truncated if needed)
  ctx.fillStyle = "#666666";
  ctx.font = URL_FONT;
  const maxUrlWidth = width - (codeX - x) - TAG_PADDING;
  let displayUrl = url;
  while (ctx.measureText(displayUrl).width > maxUrlWidth && displayUrl.length > 10) {
    displayUrl = displayUrl.slice(0, -4) + "...";
  }
  ctx.fillText(displayUrl, codeX, y + 20);
}

/**
 * Calculate the height of the tag strip that will be appended.
 */
export function getTagStripHeight(mode: TagMode): number {
  return mode === "none" ? 0 : TAG_STRIP_HEIGHT;
}

// ---------------------------------------------------------------------------
// createTaggedImage -- composites tag strip onto a source image
// ---------------------------------------------------------------------------

/**
 * Generate a complete tagged image from a source Blob.
 *
 * 1. Draws the original image on a canvas
 * 2. Appends a tag strip below the plot content
 * 3. Embeds invisible PNG metadata via threadMetadata.ts
 * 4. Returns the tagged Blob
 */
export async function createTaggedImage(
  sourceBlob: Blob,
  artifactId: string,
  projectId: string,
  mode?: string,
): Promise<Blob> {
  const tagMode = (mode ?? "qr") as TagMode;

  if (tagMode === "none") {
    return sourceBlob;
  }

  // Load the source image
  const bitmap = await createImageBitmap(sourceBlob);
  const stripHeight = getTagStripHeight(tagMode);

  // Create a canvas with extra height for the tag strip
  const canvas = document.createElement("canvas");
  canvas.width = bitmap.width;
  canvas.height = bitmap.height + stripHeight;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Failed to create canvas context");

  // Draw original image
  ctx.drawImage(bitmap, 0, 0);

  // Draw tag strip below the image
  await drawProvenanceTag(ctx, 0, bitmap.height, bitmap.width, artifactId, tagMode);

  // Convert canvas to PNG blob
  const taggedBlob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Canvas toBlob returned null"));
    }, "image/png");
  });

  // Embed invisible metadata
  const meta: ThreadMetadata = {
    version: "1.0",
    assetUrl: buildAssetUrl(artifactId),
    projectId,
    artifactId,
    createdAt: new Date().toISOString(),
  };

  return embedPngMetadata(taggedBlob, meta);
}

// ---------------------------------------------------------------------------
// React component for inline preview
// ---------------------------------------------------------------------------

/**
 * ProvenanceTag -- renders a provenance tag (QR code and/or ArUco marker)
 * inline in the UI for preview purposes.
 */
export function ProvenanceTag({
  artifactId,
  projectId: _projectId,
  mode = "qr",
  size = 80,
  showLabel = true,
}: ProvenanceTagProps) {
  void _projectId; // Reserved for future use (e.g. project-scoped tags)
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const url = useMemo(() => buildAssetUrl(artifactId), [artifactId]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const labelHeight = showLabel ? 18 : 0;
    const totalWidth = mode === "both" ? size * 2 + 8 : size;
    const totalHeight = size + labelHeight;

    canvas.width = totalWidth * dpr;
    canvas.height = totalHeight * dpr;
    canvas.style.width = `${totalWidth}px`;
    canvas.style.height = `${totalHeight}px`;
    ctx.scale(dpr, dpr);

    // Clear
    ctx.clearRect(0, 0, totalWidth, totalHeight);

    let x = 0;

    if (mode === "qr" || mode === "both") {
      drawQROnCanvas(ctx, url, x, 0, size);
      x += size + 8;
    }

    if (mode === "aruco" || mode === "both") {
      drawArucoOnCanvas(ctx, x, 0, size, artifactId);
    }

    if (showLabel) {
      ctx.fillStyle = "#666666";
      ctx.font = "9px system-ui, -apple-system, sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText("Thread", 0, size + 4);
    }
  }, [artifactId, url, mode, size, showLabel]);

  useEffect(() => {
    draw();
  }, [draw]);

  if (mode === "none") return null;

  const labelHeight = showLabel ? 18 : 0;
  const totalWidth = mode === "both" ? size * 2 + 8 : size;
  const totalHeight = size + labelHeight;

  return (
    <canvas
      ref={canvasRef}
      style={{ width: totalWidth, height: totalHeight }}
      data-testid="provenance-tag"
      aria-label={`Thread provenance tag for artifact ${artifactId}`}
    />
  );
}
